import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, ShieldCheck, Repeat, Sparkles, Star, Flame, Lock } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    key: "monitor_monthly",
    type: "sub",
    name: "Monitoring",
    tag: "Monthly",
    price: 29, unit: "/mo",
    tagline: "Ongoing freshness verification.",
    highlights: [
      "Live Freshness Score refreshed continuously",
      "Air, humidity, smoke & cleaning telemetry",
      "Alerts pushed to your dashboard and inbox",
      "Public certification page auto-updated",
    ],
    color: "emerald",
    testid: "plan-monitor-monthly",
  },
  {
    key: "monitor_yearly",
    type: "sub",
    name: "Monitoring",
    tag: "Yearly · Save $58",
    price: 290, unit: "/yr",
    tagline: "Best value for full-time hosts.",
    highlights: [
      "Everything in Monthly",
      "2 months free vs. monthly billing",
      "Priority support & platform updates",
      "Discount on Guest Smell Verify™ device",
    ],
    color: "navy",
    featured: true,
    testid: "plan-monitor-yearly",
  },
  {
    key: "cert_standard",
    type: "one",
    name: "Certification",
    tag: "Standard",
    price: 49, unit: "one-time",
    tagline: "Get certified. Display the badge.",
    highlights: [
      "One-time certification application review",
      "Guest Smell Certified™ badge for listings",
      "Shareable public certification page",
      "Instant review for eligible properties",
    ],
    color: "emerald",
    testid: "plan-cert-standard",
  },
  {
    key: "cert_premium",
    type: "one",
    name: "Certification",
    tag: "Premium",
    price: 129, unit: "one-time",
    tagline: "Premium tier + priority review.",
    highlights: [
      "Premium Certified™ gold badge",
      "Priority admin review (24h target)",
      "Enhanced certification page + host bio",
      "Featured in curated collections",
    ],
    color: "gold",
    testid: "plan-cert-premium",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    api.get("/founding/status")
      .then(({ data }) => setFounding(data))
      .catch(() => setFounding(null));
  }, []);

  const buy = (plan) => {
    if (!user) {
      toast.info("Create a host account to subscribe");
      return nav("/register");
    }
    if (user.role !== "host" && user.role !== "admin") {
      return toast.error("Host accounts only. Switch to a host account to subscribe.");
    }
    if (plan.type === "one") {
      return nav("/host");
    }
    // Open blank tab within user gesture — bypasses mobile popup blockers.
    const win = window.open("", "_blank");
    setLoading(plan.key);
    api.post("/payments/checkout", { lookup_key: plan.key, origin_url: window.location.origin })
      .then(({ data }) => {
        if (win && !win.closed) {
          win.location.href = data.checkout_url;
          toast.success("Checkout opened in a new tab");
        } else {
          try { window.top.location.href = data.checkout_url; }
          catch { window.location.href = data.checkout_url; }
        }
      })
      .catch((e) => { if (win && !win.closed) win.close(); toast.error(fmtErr(e)); })
      .finally(() => setLoading(null));
  };

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Pricing</div>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight text-[#0A1128] leading-[1.05]">
            Reasonable pricing. <br />
            <span className="italic text-emerald-600">Priceless</span> trust.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Certify once, then keep your Freshness Score live year-round. Cancel any time.
          </p>
        </div>

        {/* Founding 20 — full-width lifetime-price banner above the grid. */}
        {founding && (
          <div
            data-testid="founding-banner"
            className={`mt-10 relative overflow-hidden rounded-3xl border ${
              founding.available
                ? "bg-gradient-to-br from-[#0A1128] via-[#0F1A3A] to-[#0A1128] border-[#D4AF37]/40 shadow-2xl"
                : "bg-slate-900 border-slate-700 opacity-90"
            }`}
          >
            {/* Diagonal gold accent (only when available) */}
            {founding.available && (
              <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            )}
            <div className="relative px-6 py-8 md:px-10 md:py-10 flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {founding.available ? (
                    <Flame className="w-4 h-4 text-[#D4AF37]" />
                  ) : (
                    <Lock className="w-4 h-4 text-slate-400" />
                  )}
                  <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] font-bold">
                    {founding.available ? `Founding ${founding.cap} · Lifetime lock` : "Founding tier claimed"}
                  </span>
                </div>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl text-white leading-tight">
                  {founding.available ? (
                    <>Monitor forever for <span className="text-[#D4AF37]">$19/mo</span>.</>
                  ) : (
                    <>The Founding 20 are gone — but the standard tiers are ready.</>
                  )}
                </h2>
                <p className="mt-3 text-slate-300 text-sm md:text-base max-w-xl">
                  {founding.available ? (
                    <>Only the first {founding.cap} hosts get this price. Your rate is locked <b>as long as your subscription stays active</b> — no annual hikes, no renegotiations. First in, first paid, first celebrated in every launch post.</>
                  ) : (
                    <>Thank you to the {founding.cap} hosts who claimed the Founding rate. Monthly ({" "}$29/mo) and Yearly ({" "}$290/yr, 2 months free) are still available below and include every feature.</>
                  )}
                </p>
              </div>

              <div className="md:w-72 shrink-0">
                <div className={`p-5 rounded-2xl border ${founding.available ? "bg-white/5 border-[#D4AF37]/30" : "bg-white/5 border-slate-700"}`}>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-slate-400 font-semibold">Spots remaining</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <div
                      className={`font-serif text-5xl leading-none ${founding.available ? "text-[#D4AF37]" : "text-slate-500"}`}
                      data-testid="founding-remaining"
                    >
                      {founding.remaining}
                    </div>
                    <div className="text-sm text-slate-400">of {founding.cap}</div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${founding.available ? "bg-[#D4AF37]" : "bg-slate-500"}`}
                      style={{ width: `${Math.round((founding.sold / founding.cap) * 100)}%` }}
                    />
                  </div>
                  <Button
                    onClick={() => founding.available && buy({ key: "monitor_founding", type: "sub", testid: "plan-monitor-founding" })}
                    disabled={!founding.available || loading === "monitor_founding"}
                    data-testid="founding-cta"
                    className={`mt-5 w-full h-11 rounded-full font-semibold ${
                      founding.available
                        ? "bg-[#D4AF37] hover:bg-[#c19d2e] text-[#0A1128]"
                        : "bg-slate-700 text-slate-400 cursor-not-allowed"
                    }`}
                  >
                    {loading === "monitor_founding"
                      ? "Redirecting…"
                      : founding.available
                        ? "Claim my Founding spot →"
                        : "Sold out"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((p) => {
            const dark = p.color === "navy";
            const gold = p.color === "gold";
            return (
              <div key={p.key} data-testid={p.testid}
                className={`relative rounded-3xl p-8 border transition-transform duration-200 hover:-translate-y-1 flex flex-col ${
                  dark ? "bg-[#0A1128] text-white border-[#0A1128] shadow-2xl"
                       : gold ? "bg-white border-[#D4AF37]"
                              : "bg-white border-slate-200"
                }`}>
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 bg-[#D4AF37] text-[#0A1128] text-[10px] tracking-[0.28em] uppercase font-semibold">
                    <Star className="w-3 h-3 inline -mt-0.5 mr-1" /> Best value
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {p.type === "sub" ? (
                    <Repeat className={`w-4 h-4 ${dark ? "text-[#D4AF37]" : "text-emerald-600"}`} />
                  ) : gold ? (
                    <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  )}
                  <span className={`text-[10px] tracking-[0.24em] uppercase font-semibold ${dark ? "text-slate-300" : "text-slate-500"}`}>{p.tag}</span>
                </div>
                <h3 className={`mt-4 font-serif text-2xl ${dark ? "text-white" : "text-[#0A1128]"}`}>{p.name}</h3>
                <p className={`text-sm mt-1 ${dark ? "text-slate-300" : "text-slate-500"}`}>{p.tagline}</p>

                <div className={`mt-6 flex items-end gap-1 ${dark ? "text-white" : "text-[#0A1128]"}`}>
                  <div className="font-serif text-5xl leading-none">${p.price}</div>
                  <div className={`text-sm mb-1 ${dark ? "text-slate-300" : "text-slate-500"}`}>{p.unit}</div>
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-[#D4AF37]" : gold ? "text-[#D4AF37]" : "text-emerald-500"}`} />
                      <span className={dark ? "text-slate-200" : "text-slate-700"}>{h}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => buy(p)}
                  disabled={loading === p.key}
                  data-testid={`${p.testid}-cta`}
                  className={`mt-8 w-full h-11 rounded-full font-medium ${
                    dark ? "bg-[#D4AF37] hover:bg-[#c19d2e] text-[#0A1128]"
                         : gold ? "bg-[#0A1128] hover:bg-[#1A2A3A] text-white"
                                : "bg-emerald-600 hover:bg-emerald-700 text-white"
                  }`}
                >
                  {loading === p.key
                    ? "Redirecting…"
                    : p.type === "sub"
                      ? (user?.role === "host" ? "Subscribe" : "Start subscribing")
                      : "Certify a property"}
                </Button>
              </div>
            );
          })}
        </div>

        {/* FAQ / footer note */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { q: "Do I need a device to certify?", a: "No — MVP certification runs on manually validated telemetry. When Guest Smell Verify™ ships, subscribers get device discounts." },
            { q: "Can I cancel anytime?", a: "Yes. Monthly and yearly plans cancel at the end of the current period. No commitment beyond one billing cycle." },
            { q: "What payment methods?", a: "All major credit & debit cards via Stripe. Test payments in preview use card 4242 4242 4242 4242." },
          ].map((f) => (
            <div key={f.q} className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="font-serif text-lg text-[#0A1128]">{f.q}</div>
              <p className="mt-2 text-sm text-slate-600">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
